import {
  Sparkles,
  Droplets,
  PartyPopper,
  GraduationCap,
  Home,
  UtensilsCrossed,
  Sofa,
  Users,
  ShoppingBag,
  Package,
  Briefcase,
  LayoutGrid,
  ArrowLeft,
  ArrowRight,
  Check,
} from "lucide-react";
import { READY_EXAMPLES, type ReadyExamplePreset } from "../readyExamples";

const SECTOR_ICONS: Record<string, typeof Sparkles> = {
  beauty: Sparkles,
  cleaning: Droplets,
  event: PartyPopper,
  education: GraduationCap,
  realestate: Home,
  food: UtensilsCrossed,
  furniture: Sofa,
  network: Users,
  onlinesales: ShoppingBag,
  localproducts: Package,
  professional: Briefcase,
  other: LayoutGrid,
};

export default function ReadyExamplesScreen({
  onBack,
  onSelect,
}: {
  onBack: () => void;
  onSelect: (presetId: string) => void;
}) {
  return (
    <div className="min-h-screen bg-primary-950 text-background-50">
      {/* Arka plan ışıkları */}
      <div className="pointer-events-none fixed -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-accent-500/10 blur-3xl" />
      <div className="pointer-events-none fixed -bottom-48 -left-40 h-[480px] w-[480px] rounded-full bg-primary-500/25 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-5xl px-6 py-12 md:py-16">
        <button
          onClick={onBack}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-background-300 transition hover:text-background-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Geri
        </button>

        <div className="animate-fade-in-up mt-6 text-center">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            Hazır Bir Demo Seçin
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-300 md:text-base">
            Bilgi girmeden sektörünüzden hazırlanmış örneği hemen deneyin.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {READY_EXAMPLES.map((preset) => (
            <ReadyCard key={preset.id} preset={preset} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReadyCard({
  preset,
  onSelect,
}: {
  preset: ReadyExamplePreset;
  onSelect: (presetId: string) => void;
}) {
  const Icon = SECTOR_ICONS[preset.sectorId] ?? LayoutGrid;
  const enabled = preset.enabled;

  return (
    <button
      type="button"
      onClick={() => enabled && onSelect(preset.id)}
      disabled={!enabled}
      className={`animate-fade-in-up flex flex-col items-start rounded-2xl border p-6 text-left transition ${
        enabled
          ? "cursor-pointer border-background-700 bg-background-900/40 hover:border-accent-400 hover:bg-background-900/70"
          : "cursor-not-allowed border-background-800 bg-background-900/20 opacity-60"
      }`}
    >
      {enabled && preset.logo ? (
        <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-background-50">
          <img
            src={preset.logo}
            alt={`${preset.businessName} logo`}
            className="h-full w-full object-cover object-top"
          />
        </span>
      ) : (
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-xl ${
            enabled ? "bg-accent-500/15 text-accent-300" : "bg-background-800 text-background-500"
          }`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </span>
      )}

      {enabled && (
        <span className="mt-3 text-xs font-medium text-background-400">
          {preset.businessName}
        </span>
      )}

      <span className="mt-4 font-heading text-base font-bold leading-snug text-background-50">
        {preset.title}
      </span>

      {enabled && (
        <span className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-background-800 px-2.5 py-1 text-[11px] font-semibold text-background-300">
            {preset.serviceCategories.length} Hizmet Galerisi
          </span>
          <span className="rounded-full bg-background-800 px-2.5 py-1 text-[11px] font-semibold text-background-300">
            {preset.videos.length} Video
          </span>
          <span className="rounded-full bg-background-800 px-2.5 py-1 text-[11px] font-semibold text-background-300">
            {preset.campaigns.length} Kampanya
          </span>
        </span>
      )}

      <span className="mt-auto flex items-center gap-1.5 pt-5 text-xs font-semibold">
        {enabled ? (
          <span className="flex items-center gap-1.5 text-accent-300">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 text-primary-950">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            Hemen Dene
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        ) : (
          <span className="flex items-center gap-1.5 rounded-full bg-background-800 px-2.5 py-1 text-background-400">
            Hazırlanıyor
          </span>
        )}
      </span>
    </button>
  );
}